import { useRequiredFieldState } from '@cashew/hooks';
import React, {
	useEffect,
	useState,
	useCallback,
	type ChangeEvent,
	useMemo,
} from 'react';

import { useTranslation } from 'react-i18next';
import Input from '@cashew/input';
import {
	type DefaultSectionProps,
	type ErrorWithMessage,
	isStringUnknownRecord,
	type MerchantBank,
	merchantBankEndpoint,
	merchantEndpoint,
	useToasterErrorState,
	useToasterSuccessState,
} from './util';
import OnboardingForm from './OnboardingForm';
import SelectComponent from '../../components/SelectComponent';

const { api } = require('@cashew/utils');

export interface BankInfo {
	acquirerBank: string;
	bankName: string;
	ibanNumber: string;
	bank: string;
	accountHolder: string;
	accountNumber: string;
}

interface DataWithBankInfo {
	data: BankInfo;
}

export const hasDataWithBankInfo = (
	entry: unknown,
): entry is DataWithBankInfo =>
	isStringUnknownRecord(entry) &&
	isStringUnknownRecord(entry.data) &&
	typeof entry.data.acquirerBank === 'string' &&
	typeof entry.data.bankName === 'string' &&
	typeof entry.data.ibanNumber === 'string' &&
	typeof entry.data.bank === 'string' &&
	typeof entry.data.accountHolder === 'string' &&
	typeof entry.data.accountNumber === 'string';

/**
 * Orders Page RefundPage
 */

const BankSection = ({
	merchantData,
	isLoading,
	setIsSectionValid,
}: DefaultSectionProps) => {
	const { t } = useTranslation();

	const [isSaving, setIsSaving] = useState(false);
	const [ErrorToaster, setToasterError] = useToasterErrorState(setIsSaving);
	const [SuccessToaster, setToasterSuccess] = useToasterSuccessState();

	const [acquirerBank, setAcquirerBank, acquirerBankError] =
		useRequiredFieldState(merchantData?.acquirerBank ?? '', t);
	const [bankName, setBankName, bankNameError] = useRequiredFieldState(
		merchantData?.bankName ?? '',
		t,
	);
	const [ibanNumber, setIBANNumber, ibanNumberError] = useRequiredFieldState(
		merchantData?.ibanNumber ?? '',
		t,
	);
	const [accountNumber, setAccountNumber, accountNumberError] =
		useRequiredFieldState(merchantData?.accountNumber ?? '', t);
	const [bank, setBank, bankError] = useRequiredFieldState(
		merchantData?.bank ?? '',
		t,
	);
	const [accountHolder, setAccountHolder, accountHolderError] =
		useRequiredFieldState(merchantData?.accountHolder ?? '', t);

	const [bankOptions, setBankOptions] = useState<MerchantBank[]>([]);

	useEffect(() => {
		setAcquirerBank(merchantData?.acquirerBank ?? '');
		setBankName(merchantData?.bankName ?? '');
		setIBANNumber(merchantData?.ibanNumber ?? '');
		setAccountNumber(merchantData?.accountNumber ?? '');
		setBank(merchantData?.bank ?? '');
		setAccountHolder(merchantData?.accountHolder ?? '');
	}, [merchantData]);

	useEffect(() => {
		api
			.get({ endpoint: merchantBankEndpoint })
			.then((resp: { data: MerchantBank[] }) => {
				if (Array.isArray(resp?.data)) {
					setBankOptions(resp.data);
				}
			})
			.catch((error: ErrorWithMessage) =>
				setToasterError(t('onboardingForm.error.title.loading'), error),
			);
	}, [setToasterError, t]);

	const acquirerBankOnSelect = useCallback(
		(key: string) => setAcquirerBank(key),
		[setAcquirerBank],
	);
	const acquirerBankDropdownData = useMemo(
		() => [
			{
				key: '',
				value: t('merchantOnboarding.bank.field.acquirerBank'),
			},
			...bankOptions.map(({ key, value }) => ({ key, value })),
		],
		[t, bankOptions],
	);
	const bankNameOnChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => setBankName(e.target.value),
		[setBankName],
	);
	const ibanNumberOnChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => setIBANNumber(e.target.value),
		[setIBANNumber],
	);
	const accountNumberOnChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => setAccountNumber(e.target.value),
		[setAccountNumber],
	);
	const bankOnSelect = useCallback((key: string) => setBank(key), [setBank]);
	const bankDropdownData = useMemo(
		() => [
			{
				key: '',
				value: t('merchantOnboarding.bank.field.bankName'),
			},
			...bankOptions.map(({ key, value }) => ({ key, value })),
		],
		[t, bankOptions],
	);
	const accountHolderOnChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => setAccountHolder(e.target.value),
		[setAccountHolder],
	);

	const haveErrors = useMemo(
		() =>
			acquirerBankError.hasError ||
			bankNameError.hasError ||
			ibanNumberError.hasError ||
			accountNumberError.hasError ||
			bankError.hasError ||
			accountHolderError.hasError,
		[
			acquirerBankError.hasError,
			bankNameError.hasError,
			ibanNumberError.hasError,
			accountNumberError.hasError,
			bankError.hasError,
			accountHolderError.hasError,
		],
	);

	const saveOnClick = useCallback(() => {
		if (!isLoading && merchantData) {
			setIsSaving(true);
			api
				.patch({
					endpoint: merchantEndpoint(`${merchantData.id}`),
					params: {
						acquirerBank,
						bankName,
						ibanNumber,
						accountNumber,
						bank,
						accountHolder,
					},
				})
				.then(() => {
					setIsSaving(false);
					setToasterSuccess(
						t('onboardingForm.success.title.saving'),
						t('onboardingForm.success.message.saving'),
					);
				})
				.catch((error: any) =>
					setToasterError(t('onboardingForm.error.title.saving'), error),
				);
		}
	}, [
		haveErrors,
		acquirerBank,
		bankName,
		ibanNumber,
		accountNumber,
		bank,
		accountHolder,
		setToasterSuccess,
	]);

	useEffect(() => setIsSectionValid(!haveErrors), [haveErrors]);

	return (
		<OnboardingForm
			saveDisabled={haveErrors}
			isSaving={isSaving}
			saveOnClick={saveOnClick}
		>
			{(formClassname) => (
				<>
					<ErrorToaster />
					<SuccessToaster />
					<div className={`${formClassname}__line`}>
						<strong className={`${formClassname}__label-text`}>
							{t('merchantOnboarding.bank.section.bank')}
						</strong>
					</div>
					<div className={`${formClassname}__line`}>
						<div>
							<SelectComponent
								label={t('merchantOnboarding.bank.field.acquirerBank')}
								data={acquirerBankDropdownData}
								selected={acquirerBank}
								onSelect={acquirerBankOnSelect}
								error={acquirerBankError.message}
							/>
						</div>
						<div>
							<Input
								placeholder={t('merchantOnboarding.bank.field.bankName')}
								name="bankName"
								value={bankName}
								onChange={bankNameOnChange}
								error={bankNameError.message}
							/>
						</div>
					</div>
					<div className={`${formClassname}__separator`} />
					<div className={`${formClassname}__line`}>
						<strong className={`${formClassname}__label-text`}>
							{t('merchantOnboarding.bank.section.settlementDetails')}
						</strong>
					</div>
					<div className={`${formClassname}__line`}>
						<div className={`${formClassname}__col`}>
							<Input
								placeholder={t('merchantOnboarding.bank.field.ibanNumber')}
								name="ibanNumber"
								value={ibanNumber}
								onChange={ibanNumberOnChange}
								error={ibanNumberError.message}
							/>
						</div>
						<div>
							<Input
								placeholder={t('merchantOnboarding.bank.field.accountNumber')}
								name="accountNumber"
								value={accountNumber}
								onChange={accountNumberOnChange}
								error={accountNumberError.message}
							/>
						</div>
					</div>
					<div className={`${formClassname}__line`}>
						<div>
							<SelectComponent
								label={t('merchantOnboarding.bank.field.acquirerBank')}
								data={bankDropdownData}
								selected={bank}
								onSelect={bankOnSelect}
								error={bankError.message}
							/>
						</div>
						<div />
					</div>
					<div className={`${formClassname}__line`}>
						<div>
							<Input
								placeholder={t('merchantOnboarding.bank.field.accountHolder')}
								name="accountHolder"
								value={accountHolder}
								onChange={accountHolderOnChange}
								error={accountHolderError.message}
							/>
						</div>
						<div />
					</div>
				</>
			)}
		</OnboardingForm>
	);
};

export default BankSection;
